import { EventEmitter, Injectable } from '@angular/core';
import { Comment } from '@models/Sessions/Idea/Comment';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Session } from '@models/Sessions/Session';
import { AssetsPipe } from '@pipes/assets.pipe';
import pdfMake from 'pdfmake/build/pdfmake.js';
import vfs from 'pdfmake/build/vfs_fonts.js';

import { ConfigService } from '../config.service';
import { PdfReportImageService } from './images.pdfreport.service';

pdfMake.vfs = vfs.pdfMake.vfs;

pdfMake.fonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-Italic.ttf',
  },
};

/**
 * represents a task during creating a pdf report
 * for a session.
 */
export class PdfReportTask<T> {
  private _start = new EventEmitter<T>();
  private _finish = new EventEmitter<T>();
  private _newSubTask = new EventEmitter<PdfReportTask<any>>();

  private _started = false;
  private _selfFinished = false;
  private _finished = false;

  private _parent: PdfReportTask<any>;
  private _subtasks: PdfReportTask<any>[] = [];

  /**
   * @param _description the description of the task
   * @param _subject the subject of the task. this object will be passed as the emitters of the task emit.
   */
  constructor(
    private _description: string,
    private _subject?: T,
  ) {}

  /**
   * will start the task, if not already started,
   * and emit the proper event.
   */
  public start(): PdfReportTask<T> {
    if (!this.started) {
      this._started = true;
      this._start.emit(this._subject);
    }
    return this;
  }

  /**
   * will finish the task, if not already finished,
   * and emit proper event.
   */
  public finish(): PdfReportTask<T> {
    if (!this.started) {
      return null;
    }

    if (!this.finished) {
      this._selfFinished = true;

      if (this.subtasksFinished) {
        this._finished = true;
        this._finish.emit(this._subject);
      }
    }
    return this;
  }

  /**
   * will add a new subtask to this task, and emit proper
   * event.
   *
   * @param subtask
   * @returns the added subtask
   */
  public add(subtask: PdfReportTask<any>): PdfReportTask<any> {
    if (!this.subtasks.includes(subtask)) {
      this.subtasks.push(subtask);
      subtask._parent = this;

      subtask.onFinish.subscribe(() => {
        if (this._selfFinished) {
          this.finish();
        }
      });

      this._newSubTask.emit(subtask);
    }

    return subtask;
  }

  /**
   * @returns whether the task has started.
   */
  public get started(): boolean {
    return this._started;
  }

  /**
   * @returns whether the task has finished.
   */
  public get finished(): boolean {
    return this._finished;
  }

  /**
   * @returns whether the task is running.
   */
  public get running(): boolean {
    return this.started && !this.finished;
  }

  /**
   * @returns whether the subject of the task.
   */
  public get subject(): T {
    return this._subject;
  }

  /**
   * @returns the depth of the task in the task tree it belongs to.
   */
  public get depth(): number {
    if (!this._parent) {
      return 0;
    } else {
      return this._parent.depth + 1;
    }
  }

  /**
   * @returns whether the description of the task.
   */
  public get description(): string {
    return this._description;
  }

  /**
   * @returns the subtasks of this task.
   */
  public get subtasks(): PdfReportTask<any>[] {
    return this._subtasks;
  }

  /**
   * @returns whether all subtasks have finished.
   */
  public get subtasksFinished(): boolean {
    return !this.subtasks.some(task => !task.finished);
  }

  /**
   * this emitter emits when the task is started. the emitted value
   * will be the subject of the task.
   */
  public get onStart(): EventEmitter<T> {
    return this._start;
  }

  /**
   * this emitter emits when the task is finished. the emitted value
   * will be the subject of the task.
   */
  public get onFinish(): EventEmitter<T> {
    return this._finish;
  }

  /**
   * this emitter emits when a new sub-task is added. the emitted value
   * will be the task.
   */
  public get onNewSubTask(): EventEmitter<PdfReportTask<any>> {
    return this._newSubTask;
  }
}

@Injectable()
export class PdfReportService {
  postfix = '.pdf';
  remoteImages = {
    promised: {},
  };

  ideaFields = [
    {
      name: 'Four Ws',
      config: 'for_ws',
      properties: [
        { label: 'Who', property: 'who' },
        { label: 'What', property: 'what' },
        { label: 'Why', property: 'why' },
        { label: 'Where', property: 'where' },
      ],
    },
    {
      name: 'Innovation Sweetspot',
      config: 'innovation_sweet_spot_medical',
      properties: [
        { label: 'Medical Need', property: 'medical_need' },
        { label: 'Technical Feasibility', property: 'technical_feasibility' },
        { label: 'Business Opportunity', property: 'business_opportunity' },
      ],
    },
    {
      name: 'Implementation Steps',
      config: 'implementation_steps_medical',
      properties: [
        { label: 'Goal', property: 'goal' },
        { label: 'Solution', property: 'solution' },
        { label: 'Project Framework', property: 'project_framework' },
        { label: 'Knowledge Triangle', property: 'knowledge_triangle' },
        { label: 'Healthcare Ecosystem', property: 'healthcare_ecosystem' },
        { label: 'Impact', property: 'impact' },
      ],
    },
  ];

  /**
   * the styles for various components in the PDF.
   */
  styles = {
    centered: { alignment: 'center' },
    highlight: { color: '#23a393' },
    superfaded: { color: '#bdbdbd' },
    faded: { color: '#9e9e9e' },
    smoky: { color: '#757575' },
    black: { color: '#424242' },
    bold: { bold: true },
    thin: { weight: 100 },
    big: { fontSize: 24 },
    huge: { fontSize: 48 },
    medium: { fontSize: 18 },
    small: { fontSize: 12 },
    tiny: { fontSize: 10 },
    supertiny: { fontSize: 8 },

    components: {
      label: ['black', 'bold', 'medium'],
      small_label: ['black', 'bold'],
      email: ['tiny', 'faded'],
      no_info: ['faded'],
      session: {
        title: ['huge', 'highlight'],
        one_liner: ['thin', 'smoky', 'medium'],
        description: ['black', 'thin', 'small'],
      },
      idea: {
        title: ['big', 'black'],
        one_liner: ['thin', 'highlight'],
        description: ['black', 'thin', 'small'],
      },
    },
  };

  constructor(
    private appConfig: ConfigService,
    private images: PdfReportImageService,
    private assetsPipe: AssetsPipe,
  ) {}

  /**
   * creates a PDF Make text element with given style.
   * this is a wrapper function to ease up the process of swapping
   * PDF Make in the future.
   * @param text the text to be displayed.
   * @param style the style of the text.
   */
  text(text, style?) {
    return {
      text: text,
      style: style,
    };
  }

  /**
   * creates a PDF Make unordered list element.
   * this is a wrapper function to ease up the process of swapping
   * PDF Make in the future.
   * @param items the list of items to be put in the list
   */
  list(items) {
    return { ul: items };
  }

  /**
   * creates a PDF Make image component.
   * this is a wrapper function to ease up the process of swapping
   * PDF Make in the future.
   *
   * NOTE: if both width and height are given and the aspect ratio does not match
   * that of the image, the image will be fit into the given box.
   *
   * @param image the name of the image (should be one of the keys of 'pdfReportImages', @see {pdfreport.images.js} ).
   * @param width the width with which the image should be displayed.
   * @param height the height with which the image should be displayed.
   */
  image(image, width, height?) {
    if (height) {
      return {
        image: image,
        fit: [width, height],
      };
    } else {
      return {
        image: image,
        width: width,
      };
    }
  }

  /**
   * @returns the url of the image of a given idea.
   * @param idea
   */
  ideaImageUrl(idea: Idea) {
    return this.assetsPipe.transform(
      idea.thumbnail_id || 'image/default_idea_thumbnail.png',
    );
  }

  /**
   * @returns the url of the logo of the organizer of the session, undefined if no organizer specified.
   * @param session
   */
  sessionOrganizerLogoUrl(session: Session) {
    if (session.config.organizer && session.config.organizer.enabled) {
      return this.assetsPipe.transform(session.config.organizer.logo_id);
    } else {
      return undefined;
    }
  }

  participantRoleText(participant) {
    if (participant.role === this.appConfig.roles.SESSION_ADMIN) {
      return 'Administrator';
    } else if (participant.role === this.appConfig.roles.SESSION_MODERATOR) {
      return 'Moderator';
    } else if (participant.role === this.appConfig.roles.SESSION_PARTICIPANT) {
      return 'Participant';
    } else if (
      participant.role === this.appConfig.roles.SESSION_EXTERNAL_EVALUATOR
    ) {
      return 'External Evaluator';
    }
    return null;
  }

  /**
   * maps a `participant` object to PDF compatible elements, also displaying their role
   * @param participant
   */
  participantAndRoleElement(participant, participantStyle?) {
    if (participant && participant.user) {
      return this.text([
        this.text(
          (participant.user.first_name || '') +
            ' ' +
            participant.user.name +
            (participant.user.organization
              ? ` (${participant.user.organization})`
              : '') +
            ` -- ${this.participantRoleText(participant)}` ||
            'Anonymous Participant',
          participantStyle || [],
        ),
      ]);
    }
    return this.text([
      this.text(
        'Anonymous Participant (not signed up)',
        participantStyle || [],
      ),
    ]);
  }

  /**
   * maps a `participant` object to PDF compatible elements.
   * @param participant
   */
  participantElement(participant, participantStyle?) {
    if (participant && participant.user) {
      return this.text([
        this.text(
          (participant.user.first_name || '') +
            ' ' +
            participant.user.name +
            (participant.user.organization
              ? ` (${participant.user.organization})`
              : '') || 'Anonymous Participant',
          participantStyle || [],
        ),
        // '  ',
        // this.text(participant.user.email, this.styles.components.email
        // ),
      ]);
    }
    return this.text([
      this.text(
        'Anonymous Participant (not signed up)',
        participantStyle || [],
      ),
      // '  ',
      // this.text(participant.email, this.styles.components.email),
    ]);
  }

  /**
   * finds the information of a user from the list of participants
   * of the session.
   * @param session
   * @param _id the id of the user.
   * @returns `undefined` if no such user found.
   */
  fetchUserInfo(_id, session: Session) {
    const _candidates = session.config.participants.filter(participant => {
      return participant._id === _id;
    });
    if (_candidates.length > 0) {
      return _candidates[0];
    }
    return null;
  }

  /**
   * creates a PDF element representing the ratings of
   * a specific idea in a specific ideation session.
   * @param idea
   * @param session
   */
  ratingElement(idea: Idea, session: Session) {
    const averages = {};

    //
    // calculate averages for each axis.
    //
    Object.keys(session.config.axis).forEach(key => {
      averages[session.config.axis[key]] = idea.votes.reduce((total, item) => {
        return total + item[key] / idea.votes.length;
      }, 0);
    });

    //
    // create a text plus the average for each axis.
    //
    return [
      Object.entries(averages).map(rate => {
        return this.text([
          this.text(rate[0] + ':', this.styles.components.small_label),
          '  ',
          this.text(rate[1]),
        ]);
      }),
      this.text('based on ' + idea.votes.length + ' votes.', ['tiny', 'faded']),
    ];
  }

  /**
   * creates a PDF element representing a specific comment
   * on a specific idea in a specific ideation session.
   * @param comment
   * @param idea
   * @param session
   */
  commentElement(comment: Comment, idea: Idea, session: Session) {
    const replies = idea.comments.filter(
      c => c.parent_id && c.parent_id.toString() === comment._id.toString(),
    );
    return [
      {
        columns: [
          this.participantElement(
            this.fetchUserInfo(comment.created_by, session),
            ['bold', 'highlight'],
          ),
          {
            alignment: 'right',
            text: this.text(
              new Date(comment.created_at).toDateString() +
                '\n' +
                new Date(comment.created_at).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                }),
              ['supertiny', 'faded'],
            ),
          },
        ],
      },
      comment.text,
      '\n',
      replies.length > 0
        ? {
            layout: {
              hLineWidth: () => 0,
              vLineWidth: i => (i === 1 ? 2 : 0),
              vLineColor: () => '#eeeeee',
              paddingLeft: () => 0,
              paddingRight: () => 0,
            },
            table: {
              widths: [12, 12, '*'],
              body: [
                [
                  '',
                  '',
                  replies.map(reply =>
                    this.commentElement(reply, idea, session),
                  ),
                ],
              ],
            },
          }
        : '',
    ];
  }

  /**
   * creates a page for general information
   * of an ideation session.
   * @param session
   */
  sessionInfoPage(session: Session) {
    return {
      margin: [20, 100],
      stack: [
        this.text(session.config.title, this.styles.components.session.title),
        this.text(
          session.config.one_liner,
          this.styles.components.session.one_liner,
        ),
        '\n\n\n',
        this.text(
          session.config.description,
          this.styles.components.session.description,
        ),
        '\n\n\n',
        this.text('Participants: ', this.styles.components.label),
        '\n',
        this.list(
          session.config.participants
            .filter(participant => {
              return (
                participant.user &&
                (participant.user.first_name || participant.user.name) &&
                participant.user.email !== 'vgamper@gmail.com' &&
                participant.user.verified
              );
            })
            .map(this.participantAndRoleElement.bind(this)),
        ),
      ],
    };
  }

  /**
   * PDF Make component for
   *
   */
  ideaExtraDetails(idea: Idea, session: Session) {
    return this.ideaFields
      .filter(field => {
        if (!session.config.extra_idea_fields) {
          return false;
        }

        return !!session.config.extra_idea_fields[field.config];
      })
      .map(group => {
        return [
          this.text(group.name, this.styles.components.label),
          '\n',
          group.properties.map(property => {
            return this.text([
              this.text(property.label, this.styles.components.small_label),
              ': ',
              idea[property.property]
                ? this.text(idea[property.property])
                : this.text('Not Specified', this.styles.components.no_info),
            ]);
          }),
          '\n\n\n',
        ];
      });
  }

  /**
   * creates a page for information of a specific
   * idea in an ideation session.
   * @param idea
   * @param session
   */
  ideaPage(idea: Idea, session: Session) {
    return {
      margin: [20, 100],
      stack: [
        //
        // the image, title and the one liner of the idea.
        //
        {
          alignment: 'center',
          stack: [
            this.image(this.remoteImages[this.ideaImageUrl(idea)], 470, 192),
          ],
        },
        '\n',
        {
          alignment: 'center',
          stack: [
            this.text(
              (idea.mature ? 'Idea: ' : 'Idea Spark: ') + idea.title,
              this.styles.components.idea.title,
            ),
            this.text(idea.one_liner, this.styles.components.idea.one_liner),
          ],
        },
        '\n\n\n',

        //
        // the author of the idea, its conception date,
        // and possibly other collaborators.
        //
        this.text([
          this.text('proposed by: ', this.styles.components.small_label),
          this.participantElement(this.fetchUserInfo(idea.created_by, session)),
        ]),
        this.text([
          this.text('proposed at: ', this.styles.components.small_label),
          this.text(new Date(idea.created_at).toDateString(), [
            'faded',
            'thin',
            'tiny',
          ]),
        ]),
        idea.collaborators.length > 0
          ? [
              this.text(
                'other collaborators: ',
                this.styles.components.small_label,
              ),
              this.list(
                idea.collaborators.map(collaborator => {
                  return this.participantElement(
                    this.fetchUserInfo(collaborator.participant_id, session),
                  );
                }),
              ),
            ]
          : [],
        '\n\n',

        //
        // idea's description.
        //
        this.text('Description', this.styles.components.label),
        '\n',
        this.text(idea.description, this.styles.components.idea.description),
        '\n\n',

        //
        // extra details
        //
        this.ideaExtraDetails(idea, session),

        //
        // the average rating of the idea (across all votes).
        //
        this.text('Rating', this.styles.components.label),
        '\n',
        idea.votes.length > 0
          ? this.ratingElement(idea, session)
          : this.text(
              'No votes documented on this idea.',
              this.styles.components.no_info,
            ),
        '\n\n',

        //
        // the followers of this idea
        //
        this.text('Followers', this.styles.components.label),
        '\n',
        idea.followers.length > 0
          ? this.text(
              idea.followers.map((follower, index) => {
                if (index < idea.followers.length - 1) {
                  return this.text([
                    this.participantElement(
                      this.fetchUserInfo(follower.participant_id, session),
                    ),
                    ', ',
                  ]);
                } else {
                  return this.participantElement(
                    this.fetchUserInfo(follower.participant_id, session),
                  );
                }
              }),
            )
          : this.text(
              'No followers documented for this idea.',
              this.styles.components.no_info,
            ),
        '\n\n',

        //
        // the comments on the idea (in the order sent by server).
        //
        this.text('Discussion', this.styles.components.label),
        '\n',
        idea.comments.length > 0
          ? idea.comments
              .filter(comment => !comment.parent_id)
              .map(comment => {
                return this.commentElement(comment, idea, session);
              })
          : this.text(
              'No discussion documented on this idea.',
              this.styles.components.no_info,
            ),
      ],
      pageBreak: 'before',
    };
  }

  /**
   * generates the PDF header component for
   * an ideation session report pdf.
   * @param session
   *
   * NOTE: this function is directly dependant on PDF Make API, so
   * changing PDF Make would require a rewrite of this function.
   */
  header(session: Session) {
    return {
      margin: 10,
      columns: [
        session.config.organizer && session.config.organizer.enabled
          ? this.image(
              this.remoteImages[this.sessionOrganizerLogoUrl(session)],
              60,
            )
          : [],
        {
          width: '*',
          alignment: 'right',
          stack: [
            this.text('Ideation Session Report', ['tiny', 'faded']),
            this.text(
              session.config.title + '    ' + new Date().toDateString(),
              ['tiny', 'faded'],
            ),
            {
              canvas: [
                {
                  type: 'line',
                  x1: 0,
                  y1: 6,
                  x2: 200,
                  y2: 6,
                  lineWidth: 1,
                  lineColor: '#e0e0e0',
                },
              ],
            },
          ],
        },
      ],
    };
  }

  /**
   * generates the PDF footer component for
   * an ideation session report pdf.
   * @param session
   *
   * NOTE: this function is directly dependant on PDF Make API, so
   * changing PDF Make would require a rewrite of this function.
   */
  footer() {
    return (currentPage, pageCount) => {
      return {
        margin: 10,
        stack: [
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: -5,
                x2: 200,
                y2: -5,
                lineWidth: 1,
                lineColor: '#e0e0e0',
              },
            ],
          },
          {
            columns: [
              this.text('page ' + currentPage + ' of ' + pageCount, [
                'tiny',
                'smoky',
              ]),
              {
                margin: [10, 0],
                text: [
                  this.text('generated by WeDavinci\n', ['tiny', 'superfaded']),
                  this.text('https://www.wedavinci.com/', [
                    'supertiny',
                    'superfaded',
                  ]),
                ],
                alignment: 'right',
              },
              this.image(this.images['wedavinci'], 20),
            ],
          },
        ],
      };
    };
  }

  /**
   * will create a PDF report based on given session and open it in a new tab.
   *
   * @param session the session to whom the report belongs.
   * @returns the task object representing this task.
   */
  open(session: Session): PdfReportTask<Session> {
    if (!session.config.organizer) {
      session.config.organizer = this.appConfig.defaultOrganizer;
    }

    //
    // create the task
    //
    const task = new PdfReportTask<Session>(
      `creating pdf report for "${session.config.title}"`,
      session,
    );

    //
    // lets start the task in a new thread, also with a little wait ...
    //
    setTimeout(() => {
      //
      // start the main task.
      //
      task.start();

      //
      // get the ideas that we should add to the
      // report...
      //
      const ideas = session.data.ideas.filter(
        idea => idea.published && !idea.deleted_at,
      );

      //
      // create the task for loading images
      //
      const loadingImages = task
        .add(
          new PdfReportTask<Session>(
            `loading images of "${session.config.title}"`,
            session,
          ),
        )
        .start();

      //
      // load images of these ideas ...
      //
      this.loadImages(
        session,
        ideas,
        () => {
          //
          // mark the loading image task as finished.
          //
          loadingImages.finish();

          //
          // create the pdf object.
          //
          const pdf = pdfMake.createPdf({
            content: [
              this.sessionInfoPage(session),
              ideas.map(idea => this.ideaPage(idea, session)),
            ],
            header: this.header(session),
            footer: this.footer(),
            styles: this.styles,
          });

          //
          // create a task for downloading the pdf.
          //
          const downloadingPdf = task
            .add(
              new PdfReportTask<Session>(
                `downloading report of "${session.config.title}"`,
                session,
              ),
            )
            .start();

          //
          // download the pdf
          //
          pdf.download(this.name(session) + this.postfix, () => {
            //
            // mark the tasks as finished.
            //
            downloadingPdf.finish();
            task.finish();
          });
        },
        loadingImages,
      );
    }, 1000);

    return task;
  }

  /**
   * will determine the name of the PDF report for the session.
   * @param session the session to whom the report belongs.
   */
  name(session: Session) {
    return (
      'Ideation Session ' +
      session.config.title +
      ' Report, ' +
      new Date().toLocaleDateString()
    );
  }

  /**
   * will load remote images required for the session report.
   * we need to load images and convert them to data URI to be able
   * embed them inside the PDF. this function will populate the `remoteImages`
   * dictionary with all the data URI's of all necessary images, with its keys
   * being the url's of those images (so to avoid multiple loading).
   *
   * the `remoteImages.promised` object also holds promises that are loading images,
   * so to avoid loading an image twice before the first time is finished. the promises
   * remain in that sub-dictionary for future reference as well.
   *
   * @param session
   * @param callback
   * @param task the main task for creating the pdf report.
   */
  loadImages(
    session: Session,
    ideas: Idea[],
    callback,
    task: PdfReportTask<Session>,
  ) {
    //
    // these objects will be required inside the callback
    // tangle below, so we extract them and add them to current
    // run context.
    //
    const remoteImages = this.remoteImages;

    //
    // ok to get the image data URI we need to load an image into
    // and `img` element (which is cool since the browser's downloader will
    // take care of loading these images and also caching and stuff would be
    // automatically handled), then render the images upon a canvas, and read
    // the canvas data as a PNG's data URI format (so quality will also get
    // automatically handled). this function basically does all of that,
    // registers the promises and then the data with `remoteImages` dictionary,
    // and returns the promise.
    //
    const loadImage = (ideaOrSession: Idea | Session, isSession?: boolean) => {
      //
      // get the url of the image first
      //
      let url;
      let imageDescription;

      if (!isSession) {
        const idea = ideaOrSession as Idea;
        url = this.ideaImageUrl(idea);
        imageDescription = `idea image of "${idea.title}"`;
      } else {
        const _session = ideaOrSession as Session;
        url = this.sessionOrganizerLogoUrl(_session);
        imageDescription = `organizer logo of session "${_session.config.title}"`;
      }

      //
      // create the task, add it to the progress object and start it.
      //
      const loadingTask = task
        .add(new PdfReportTask<string>(`loading ${imageDescription}`, url))
        .start();
      //
      // so if we already have the promise, we should ignore this url.
      // note that if the image is also loaded the promise would be present here
      // as well.
      //
      if (remoteImages.promised[url]) {
        loadingTask.finish();
        return remoteImages.promised[url];
      }

      //
      // create the promise.
      //
      const promise = new Promise(resolve => {
        //
        // the `img` element we create here will download the image. it needs to be marked
        // with anonymous crossOrigin so that it can download the image and still read its data.
        // this also means the source of the image should allow CORS.
        //
        const image = document.createElement('img');
        image.setAttribute('crossOrigin', 'anonymous');

        image.onload = function () {
          //
          // lets create the preparation task.
          //
          const prepTask = loadingTask.add(
            new PdfReportTask<string>(`preparing ${imageDescription}`, url),
          );
          prepTask.start();

          //
          // the loading task itself is finished, although
          // it will wait for the prep task as well.
          //
          loadingTask.finish();

          //
          // we will then render the content of the image onto a canvas
          // to be able to later read the data in proper format for the PDF.
          //
          const canvas = document.createElement('canvas');
          canvas.width = image.width;
          canvas.height = image.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(image, 0, 0);

          //
          // we read the image data as 'PNG' basically to avoid dealing with 'JPG'
          // quality and compression controls.
          //
          const data = canvas.toDataURL('image/png');

          //
          // register the data with the dictionary,
          // resolve the promise.
          //
          remoteImages[url] = data;

          //
          // the prep task is also finished.
          //
          prepTask.finish();

          resolve(data);
        };

        //
        // there is a terrible bug that prevents chrome and safari from
        // reading CORS enabled S3 images, and this is basically the work-around.
        //
        image.src =
          url.replace(/^https:\/\//i, 'http://') +
          '?' +
          Math.random()
            .toString(36)
            .replace(/[^a-z]+/g, '')
            .substr(0, 5); // this bit is for cache busting
      });
      return promise;
    };

    const loadings = ideas.map(idea => loadImage(idea));
    if (session.config.organizer && session.config.organizer.enabled) {
      loadings.push(loadImage(session, true));
    }

    Promise.all(loadings).then(callback);
  }
}
